import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { chatApi, type ListConversationsParams } from '@/api/chat.api';
import type { SendMessageBody } from '@/types/chat.types';

export const chatKeys = {
  all: ['chats'] as const,
  lists: () => [...chatKeys.all, 'list'] as const,
  list: (params: ListConversationsParams) => [...chatKeys.lists(), params] as const,
  messages: (conversationId: string) => [...chatKeys.all, 'messages', conversationId] as const,
  labels: () => [...chatKeys.all, 'labels'] as const,
};

export function useConversations(params: ListConversationsParams = {}) {
  return useQuery({
    queryKey: chatKeys.list(params),
    queryFn: () => chatApi.listConversations(params),
    placeholderData: (prev) => prev,
  });
}

export function useMessages(conversationId: string | undefined) {
  return useQuery({
    queryKey: chatKeys.messages(conversationId || ''),
    queryFn: () => chatApi.getMessages(conversationId as string),
    enabled: !!conversationId,
  });
}

export function useLabels() {
  return useQuery({
    queryKey: chatKeys.labels(),
    queryFn: () => chatApi.getLabels(),
  });
}

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SendMessageBody) => chatApi.sendMessage(body),
    onSuccess: (_, body) => {
      qc.invalidateQueries({ queryKey: chatKeys.messages(body.conversationId) });
      qc.invalidateQueries({ queryKey: chatKeys.lists() });
    },
    onError: () => toast.error('No se pudo enviar el mensaje'),
  });
}
